<template>
  <CustomDashboardModuleItem
    :style="{
      '--header-align-items': 'flex-start',
      '--header-padding': '0',
      '--header-border-bottom': 'none',
      '--header-left-grid-column': '1fr',
      '--content-padding': '0'
    }"
    hide-collapse
  >
    <template #title>
      <div v-show="customDashboardState.isDragging" class="cdt-Title">
        {{ props.formModel.name }}
      </div>
    </template>

    <template #content>
      <div :class="{ 'cdt-Content-is-edit': isEdit }" class="cdt-Content">
        <div
          v-if="!isEdit && withActions && canEdit && !customDashboardState.isDragging"
          class="cdt-Content_Actions"
        >
          <TextModuleActions
            v-if="!customDashboardState.isDragging"
            v-model:is-edit="isEdit"
            @show-remove="onRemove"
          />
        </div>

        <DescriptionField
          ref="descriptionField"
          v-model="description"
          :hidden-features="[TIPTAP_EXTENSION_NAMES.MENTION]"
          :max-length="DESCRIPTION_FILED_LIMITS.DESCRIPTION"
          :placeholder="$t('dashboard.use_this_for_descr')"
          :readonly="!customDashboardState.userCanManageCustomDashboard"
          :show-character-counter="false"
          :show-toolbar-divider="isEdit && !isSaving"
          :sticky-toolbar="false"
          :style-type="DESCRIPTION_FIELD_TYPES.INLINE"
          move-actions-to-toolbar
          table-actions-append-to="body"
          @active="onActiveDescriptionField"
          @cancel="onCancel"
          @save="onSave"
        />
      </div>
    </template>
  </CustomDashboardModuleItem>
</template>

<script setup>
import { inject, nextTick, ref, useTemplateRef, watch } from 'vue'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
// import { SKELETON_SIZES } from '@/utils/components-configurations/skeleton-item'
import { handleError } from '@/utils/error-handling'
import { customDashboardStateInjectionKey } from '@/utils/injection-keys'
import { DESCRIPTION_FILED_LIMITS } from '@/utils/rich-edit'
import { DESCRIPTION_FIELD_TYPES, TIPTAP_EXTENSION_NAMES } from '@/utils/tiptap-utils'

import CustomDashboardModuleItem from '@/components/dashboard/custom-dashboard/CustomDashboardModuleItem'
import TextModuleActions from '@/components/dashboard/custom-dashboard/modules/moduleActions/TextModuleActions'
import DescriptionField from '@/components/objectives/forms/DescriptionField'
// import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'

// const { SM } = SKELETON_SIZES

defineOptions({
  name: 'CustomDashboardTextModule'
})

const isEdit = ref(false)
const descriptionField = useTemplateRef('descriptionField')

const props = defineProps({
  formModel: {
    type: Object,
    default: () => ({})
  },

  dashboardItem: {
    type: Object,
    default: () => ({})
  },

  withActions: {
    type: Boolean,
    default: true
  },

  canEdit: {
    type: Boolean
  }
})
const description = ref('')

watch(
  () => props.formModel,
  () => {
    description.value = props.formModel.textValue
  },
  { deep: true, immediate: true }
)

const showEditor = () => {
  nextTick(() => {
    descriptionField.value.showEditor()
  })
}

watch(
  () => isEdit.value,
  newValue => {
    if (newValue) {
      showEditor()
    }
  }
)
const customDashboardState = inject(customDashboardStateInjectionKey)

const onRemove = () => {
  customDashboardState.value.removeModule(props.formModel)
}

const onCancel = () => {
  isEdit.value = false
  nextTick(() => {
    description.value = props.formModel.textValue || ''
  })
}

const isSaving = ref(false)

const onSave = async () => {
  isSaving.value = true
  isEdit.value = false
  try {
    const api = new CustomDashboardApiHandler()

    await api.updateModule({
      id: props.dashboardItem.id,
      moduleId: props.formModel.id,
      textValue: description.value
    })

    await customDashboardState.value.reloadDashboard()
  } catch (error) {
    handleError({ error })
  } finally {
    isSaving.value = false
  }
}

const onActiveDescriptionField = payload => {
  isEdit.value = payload
}
</script>

<style lang="scss" scoped>
.cdt-Title {
  padding: 24px 20px;
}

.cdt-Content {
  --editor-active-outer-padding: 0;
  --description-field-margin-bottom: 0;
  --editor-inactive-padding: 32px;
  --editor-active-padding: 32px;
  --toolbar-padding: 0 20px 0 8px;
  --actions-flex-direction: row-reverse;
  --placeholder-hover-color: none;
  position: relative;
  --placeholder-padding: 32px;

  &-is-edit {
    --toolbar-padding: 16px 20px 0 8px;
  }
}

.cdt-Content_Actions {
  position: absolute;
  right: 20px;
  top: 20px;
  z-index: 2;
}

//.cdt-SkeletonDescriptionField {
//  margin: 34px 20px 18px;
//}
</style>
